import type { FC } from 'react';
import React, { useEffect, useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage } from 'react-intl-next';

import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';
import { ADVANCED_SEARCH } from '@confluence/named-routes';

import type { HomeViewFilters } from './homeViewsQueryHelpers';
import NoResultsImage from './images/NoResultsImage.svg';
import type { RecentFilter } from './Recent/useRecentPreferences';

const NoResultsContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

type NoResultsType = {
	message: MessageDescriptor;
	subMessage?: MessageDescriptor;
	filterText: string;
	source: string;
	filter: HomeViewFilters | RecentFilter;
};

export const NoResults: FC<NoResultsType> = ({
	message,
	subMessage,
	filterText,
	source,
	filter,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId } = useSessionData();
	const fireTrackEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'shown',
				actionSubject: `${source}NoResults`,
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const onSearchClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: `${source}SearchLink`,
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	useEffect(() => {
		fireTrackEvent();
	}, [fireTrackEvent]);
	const hasAdvancedSearchContributorSetting = 'WORKED_ON' === filter;
	const hasAdvancedSearchOwnerSetting = 'CREATED' === filter;

	const searchHref = ADVANCED_SEARCH.toUrl(
		{},
		{
			query: {
				text: filterText,
				...(hasAdvancedSearchContributorSetting && userId && { contributors: userId }),
				...(hasAdvancedSearchOwnerSetting && userId && { owners: userId }),
			},
		},
	);
	return (
		<Box xcss={NoResultsContainerStyles} paddingBlockStart="space.1000">
			<img src={NoResultsImage} alt="" />
			<Box paddingBlockStart="space.600" paddingBlockEnd="space.150">
				<Heading size="small" as="h2">
					<FormattedMessage {...message} />
				</Heading>
			</Box>
			{subMessage && (
				<FormattedMessage
					{...subMessage}
					values={{
						a: (anchorText: React.ReactNode[]) =>
							fg('dst-a11y__replace-anchor-with-link__disco') ? (
								<Link href={searchHref} onClick={onSearchClick}>
									{anchorText}
								</Link>
							) : (
								// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
								<a href={searchHref} onClick={onSearchClick}>
									{anchorText}
								</a>
							),
					}}
				/>
			)}
		</Box>
	);
};
